import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { track } from '@/lib/track';
import axios from 'axios';
import { API } from '@/lib/utils';

const LiveChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Listen for FAB menu event
  useEffect(() => {
    const handler = () => { setIsOpen(true); track('chat_open', { trigger: 'event' }); };
    window.addEventListener('open-chat', handler);
    return () => window.removeEventListener('open-chat', handler);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Add welcome message
      setMessages([
        {
          id: 'welcome',
          sender: 'bot',
          message: "Welcome to Legend Conversion! I'm here to help you explore our custom vans. How can I assist you today?",
          created_at: new Date().toISOString()
        }
      ]);
    }
  }, [isOpen, messages.length]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: `user_${Date.now()}`,
      sender: 'user',
      message: inputValue.trim(),
      created_at: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      await axios.post(`${API}/chat`, {
        session_id: sessionId,
        sender: 'user',
        message: inputValue.trim()
      });

      // Fetch updated messages including bot response
      const response = await axios.get(`${API}/chat/${sessionId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        id: `error_${Date.now()}`,
        sender: 'bot',
        message: "I'm having trouble connecting right now. Please try again or call us at (682) 369-1500.",
        created_at: new Date().toISOString()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <button
        data-testid="chat-widget-toggle"
        onClick={() => { setIsOpen(true); track('chat_open', { trigger: 'launcher' }); }}
        className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-gold hover:bg-gold-hover text-navy rounded-full shadow-xl hidden md:flex items-center justify-center transition-all duration-300 hover:scale-110 animate-pulse-gold"
        aria-label="Open chat"
      >
        <MessageCircle className="w-7 h-7" />
      </button>
    );
  }

  return (
    <div
      data-testid="chat-widget"
      className={`fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-lg shadow-2xl overflow-hidden transition-all duration-300 ${
        isMinimized ? 'h-14' : 'h-[500px] max-h-[calc(100vh-6rem)]'
      }`}
    >
      {/* Header */}
      <div className="bg-navy text-white px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gold rounded-full flex items-center justify-center">
            <MessageCircle className="w-5 h-5 text-navy" />
          </div>
          <div>
            <h4 className="font-semibold text-sm">Legend Conversion</h4>
            <p className="text-xs text-white/70">We typically reply instantly</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="chat-minimize"
            onClick={() => setIsMinimized(!isMinimized)}
            className="p-1.5 hover:bg-white/10 rounded transition-colors"
            aria-label={isMinimized ? 'Expand chat' : 'Minimize chat'}
          >
            <Minimize2 className="w-4 h-4" />
          </button>
          <button
            data-testid="chat-close"
            onClick={() => setIsOpen(false)}
            className="p-1.5 hover:bg-white/10 rounded transition-colors"
            aria-label="Close chat"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages */}
          <div 
            data-testid="chat-messages"
            className="h-[calc(100%-130px)] overflow-y-auto p-4 space-y-4 bg-gray-50"
          >
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm ${
                    msg.sender === 'user'
                      ? 'bg-gold text-navy rounded-br-sm'
                      : 'bg-white text-navy shadow-sm rounded-bl-sm border'
                  }`}
                >
                  {msg.message}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white text-navy px-4 py-2.5 rounded-2xl rounded-bl-sm shadow-sm border">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies */}
          <div className="px-4 py-2 border-t bg-white flex gap-2 overflow-x-auto">
            {['Models', 'Schedule Visit'].map((reply) => (
              <button
                key={reply}
                onClick={() => setInputValue(reply)}
                className="px-3 py-1 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-navy rounded-full whitespace-nowrap transition-colors"
              >
                {reply}
              </button>
            ))}
          </div>

          {/* Input */}
          <form onSubmit={sendMessage} className="p-3 border-t bg-white flex gap-2">
            <Input
              data-testid="chat-input"
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 border-gray-200 focus:border-gold focus:ring-gold"
              disabled={isLoading}
            />
            <Button
              data-testid="chat-send"
              type="submit"
              size="icon"
              disabled={!inputValue.trim() || isLoading}
              className="bg-gold hover:bg-gold-hover text-navy"
            >
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </>
      )}
    </div>
  );
};

export default LiveChat;
